import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useI18n } from 'hooks/useI18n';
import { useEffect, useRef, useState } from 'react';
import { getPublicPath } from 'utils/paths';
import {
  StyledCaret,
  StyledCategory,
  StyledContent,
  StyledIcon,
  StyledTrigger
} from './RolesCategory.styled';
import RolesItem from './RolesItem';

type RolesItemProps = {
  name: string;
  desc: string;
};

type RolesCategoryProps = {
  id: string;
  category: string;
  roles: RolesItemProps[];
};

type RolesProps = {
  role: RolesCategoryProps;
};

const RolesCategory = ({ role }: RolesProps) => {
  const { t } = useI18n('roles');
  const content = useRef<HTMLDivElement>(null);

  const [setActive, setActiveState] = useState('');

  function toggleAccordion(): void {
    setActiveState(setActive === '' ? 'is-active' : '');
  }

  const [currentHeight, setCurrentHeight] = useState(
    content.current?.scrollHeight
  );

  const contentChildren = useRef<HTMLDListElement>(null);

  function updateCurrentHeight(): void {
    setCurrentHeight(content.current?.scrollHeight);
  }

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateCurrentHeight();
    });
    if (contentChildren.current) {
      resizeObserver.observe(contentChildren.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const contentHeight = {
    '--content-height': `${currentHeight}px`
  } as React.CSSProperties;

  return (
    <StyledCategory id={role.id}>
      <StyledTrigger
        type="button"
        id={'button_' + role.id}
        className={`${setActive}`}
        onClick={toggleAccordion}
        aria-expanded={setActive === 'is-active'}
        aria-controls={'content_' + role.id}
      >
        <StyledIcon
          src={getPublicPath('/images/icons/roles/' + role.id + '.svg')}
          alt=""
        />
        <h2>{t(role.category)}</h2>
        <StyledCaret>
          <FontAwesomeIcon icon={faAngleDown} />
        </StyledCaret>
      </StyledTrigger>
      <StyledContent
        ref={content}
        id={'content_' + role.id}
        className={`${setActive}`}
        style={contentHeight}
        role="region"
        aria-labelledby={'button_' + role.id}
      >
        <dl ref={contentChildren}>
          {role.roles.map((r, i) => (
            <RolesItem key={i} name={r.name} desc={r.desc} />
          ))}
        </dl>
      </StyledContent>
    </StyledCategory>
  );
};

export default RolesCategory;
